import React from "react";
import { API_get_schedule, API_get_blackout_dates, API_list_availability_for_employee } from "../../constants";
import { getRightworkAPI } from "../../main/content/requests/utilities/requests";
import { formatDate, getMaxDatePlusOne } from "../../main/content/requests/utilities/helpers";
import { getRequestLocations } from "./requestLocationUtils";

const EMPTY_LOCATIONS = [];

export const RequestLocationContext = React.createContext(null);

// Only self-service request forms consume this context. Manager edits on a
// specific employee profile keep their existing store and membership together.
export function RequestLocationProvider({ stores, memberships, currentStoreId, children }) {
  const value = React.useMemo(() => ({
    stores: getRequestLocations(stores, memberships), currentStoreId,
  }), [stores, memberships, currentStoreId]);
  return <RequestLocationContext.Provider value={value}>{children}</RequestLocationContext.Provider>;
}

export function useRequestLocation(props, open, day) {
  const context = React.useContext(RequestLocationContext);
  const enabled = !!context && props.requestLocationEnabled !== false;
  const locations = enabled ? context.stores : EMPTY_LOCATIONS;
  const currentStoreId = context?.currentStoreId;
  const defaultId = locations.find((s) => String(s.id) === String(currentStoreId))?.id ?? locations[0]?.id;
  const [selectedId, setSelectedId] = React.useState(defaultId);
  const storeId = selectedId ?? defaultId;
  const [loaded, setLoaded] = React.useState(null);
  const [attempt, setAttempt] = React.useState(0);
  const needsLoad = enabled && String(storeId) !== String(currentStoreId);

  React.useEffect(() => {
    if (!open) setSelectedId(defaultId);
  }, [open, defaultId]);

  React.useEffect(() => {
    if (!open || !needsLoad || !locations.some((store) => String(store.id) === String(storeId))) return;
    let cancelled = false;
    setLoaded(null);
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 28);
    const params = {
      store_id: storeId, lower_bound_date: formatDate(today),
      upper_bound_date: formatDate(end), specific_date: formatDate(today),
      date_start: formatDate(today), date_end: formatDate(end),
      should_return_all_stores: false,
    };
    Promise.all([
      getRightworkAPI(API_get_schedule, params),
      getRightworkAPI(API_get_blackout_dates, { store_id: storeId }),
      getRightworkAPI(API_list_availability_for_employee, { store_id: storeId }),
    ]).then(([schedule, blackout, availability]) => {
      if (cancelled) return;
      const payload = schedule.data?.redux?.payload;
      const member = payload?.me?.store_team_member;
      const blackoutDates = blackout.data?.redux?.payload?.blackoutDates;
      const availabilityData = availability.data?.redux?.payload;
      if (!member || !Array.isArray(payload.schedule) || !Array.isArray(blackoutDates) || !Array.isArray(availabilityData?.current)) {
        throw new Error("Missing location settings");
      }
      const store = locations.find((s) => String(s.id) === String(storeId));
      const minDate = new Date(today);
      if (store?.request_minimum_days_in_advance) {
        minDate.setDate(minDate.getDate() + Number(store.request_minimum_days_in_advance));
      }
      setLoaded({
        storeId,
        minEffectiveDate: store?.request_minimum_days_in_advance ? minDate : getMaxDatePlusOne(payload.schedule),
        canRequestPto: member.can_request_pto,
        canRequestSicktime: member.can_request_sicktime,
        blackoutDates,
        availability: availabilityData,
      });
    }).catch(() => {
      if (!cancelled) setLoaded({ storeId, error: "Could not load this location’s request settings. Try again." });
    });
    return () => { cancelled = true; };
  }, [open, needsLoad, storeId, attempt, locations]);

  const destination = locations.find((s) => String(s.id) === String(storeId));
  const matching = loaded && String(loaded.storeId) === String(storeId);
  const loading = enabled && needsLoad && (!matching || !loaded);
  const error = enabled && !destination ? "No active location is available for this request." : needsLoad && matching ? loaded.error : null;
  const rules = needsLoad && matching && !loaded.error ? loaded : props;
  const dow = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"].indexOf(day) + 1;
  const hasDefaultAvailability = needsLoad && matching && !loaded.error
    ? (loaded.availability?.current || []).some((row) => Number(row.dow) === dow)
    : undefined;

  return {
    enabled, locations, storeId, setStoreId: setSelectedId,
    loading, error, ready: !enabled || (!!destination && !loading && !error),
    retry: () => setAttempt((value) => value + 1),
    minEffectiveDate: rules.minEffectiveDate,
    canRequestPto: rules.canRequestPto,
    canRequestSicktime: rules.canRequestSicktime,
    blackoutDates: rules.blackoutDates || [],
    hasDefaultAvailability,
    payload: (data) => enabled ? { ...data, store_id: storeId } : data,
  };
}
